import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import BackIcon from "assets/icons/back.svg?react";
import ArrowLeftIcon from "assets/icons/arrow_l.svg?react";
import settingService from "api/settingsService";
import Input from "components/Input";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import { getErrorMessage } from "utils/getErrorMessage";
import classNames from "classnames";
import { TwoFactorMethod } from "dto/Settings/TwoFactorMethod";
import Spinner from "components/Spinner";
import { IInitTotpResponse } from "dto/Settings/IInitTotpResponse";
import { ENotificationType, notification } from "utils/notification";
import ReactDOM from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";

const OtpMethod: FC<Props> = ({
  isActive,
  onCanceled,
  onSaved,
  handleRemove,
}) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!isActive) {
      initTotp();
    }
  }, []);

  const initTotp = () => {
    getInitTotp(
      {},
      {
        onSuccess(data) {
          const resp = data.data as IInitTotpResponse;
          setTotpUri(resp.uri);
        },
        onError(e: any) {
          notification({
            title: t("errors.occurred"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      }
    );
  };

  const configure = () => {
    setTotp(
      { code: totpCode },
      {
        onSuccess() {
          onSaved();
        },
        onError(e: any) {
          setErrorMessage(getErrorMessage(e.response.data));
        },
      }
    );
  };

  const { mutate: getInitTotp, isLoading: isLoadingTotp } =
    settingService.useGetInitTotp();
  const { mutate: setTotp } =
    settingService.useSetTotp();
  const [totpUri, setTotpUri] = useState<string>("");
  const [totpCode, setTotpCode] = useState<string>("");

  useEffect(() => {
    if (totpUri) {
      ReactDOM.render(
        <QRCodeCanvas value={totpUri} />,
        document.getElementById("qrcode")
      );
    }
  }, [totpUri]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.pathBar}>
        <div className={styles.backButton}>
          <BackIcon onClick={onCanceled}></BackIcon>
        </div>
        {t("settings.security.2faHeader")} <ArrowLeftIcon></ArrowLeftIcon> {t("settings.otpBreadcrumbTitle")}
      </div>

      {isActive && (
        <>
          <div className={styles.hint}>
            {t("settings.otpConfigured")}
            <br></br>
            {t("settings.otpResetRequired")}
          </div>
        </>
      )}

      {!isActive && (
        <>
          <div className={styles.hint}>
            <b>
              {t("settings.otpScanQr")}
            </b>
            <br></br>
            {t("settings.otpAppHint")}{" "}
            <a
              href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en&gl=US"
              target="_blank"
            >
              Android
            </a>{" "}
            {t("common.or")}{" "}
            <a
              href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
              target="_blank"
            >
              iOS
            </a>
            {t("settings.otpGoogleAuthenticator")}{" "}
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
            >
              Android
            </a>{" "}
            {t("common.or")}{" "}
            <a
              href="https://apps.apple.com/us/app/google-authenticator/id388497605"
              target="_blank"
            >
              iOS
            </a>
            {t("settings.otpAppHint2")}
            <br></br>
            {t("settings.otpEnterCodeTypo")}
          </div>
          <div id="qrcode" className={styles.qrcode}></div>
          {isLoadingTotp && (
            <div className={styles.loading}>
              <Spinner className={styles.spinner} />
              <p>{t("common.loadingShort")}</p>
            </div>
          )}
          <div className={styles.codeInput}>
            <Input
              type="text"
              label={t("settings.verificationCode")}
              onChange={(v) => setTotpCode(v)}
            ></Input>
          </div>
          {errorMessage && <div className={styles.warn}>{errorMessage}</div>}
        </>
      )}

      <div className={styles.buttons}>
        {isActive && (
          <div className={styles.disconnect}>
            <a onClick={() => handleRemove(TwoFactorMethod.Totp)}>
              {t("settings.removeMethod")}
            </a>
          </div>
        )}
        <HollowButton text={t("common.cancel")} onClick={onCanceled}></HollowButton>
        {!isActive && <Button text={t("common.verify")} onClick={configure}></Button>}
      </div>
      <div className={classNames(styles.hint, styles.disconnect)}></div>
    </div>
  );
};

export default OtpMethod;
